import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Button, Tag, Modal, Typography, Space, Form, Row, Col,
  Checkbox, Card, App as AntApp, Flex, Collapse,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';

const { Text } = Typography;

// Catálogo de operações (carregado dinamicamente). Cada operação tem o array
// `grupos` com os perfis que podem executá-la — o nome do perfil é o mesmo
// nome do grupo Cognito (mapeamento 1-pra-1).
interface Operacao {
  operacao_id: string;
  modulo: string;
  descricao: string;
  risco: 'CRITICO' | 'ALTO' | 'MEDIO' | 'BAIXO';
  grupos: string[];
}
const OP_MODULO_LABEL: Record<string, string> = {
  comercial: '🏢 Comercial', financeiro: '💰 Financeiro',
  admin: '⚙️ Admin', agentes: '🤖 Agentes', operacional: '⚙️ Operacional',
  administrativo: '📦 Administrativo',
};
const OP_RISCO_TAG: Record<string, string> = {
  CRITICO: 'red', ALTO: 'gold', MEDIO: 'blue', BAIXO: 'default',
};

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Aba {
  id: string;
  label: string;
  modulo: 'home' | 'financeiro' | 'comercial' | 'operacional' | 'administrativo' | 'marketing' | 'atividades' | 'admin';
}

interface Perfil {
  nome: string;
  descricao: string;
  precedence: number | null;
  usuarios_count: number;
  permissoes: string[];
}

const MODULO_LABEL: Record<string, string> = {
  home: 'Visão Geral',
  financeiro: 'Financeiro',
  comercial: 'Comercial',
  operacional: 'Operacional',
  administrativo: 'Administrativo',
  marketing: 'Marketing',
  atividades: 'Atividades',
  admin: 'Administração',
};

const MODULO_COR: Record<string, string> = {
  home: '#666',
  financeiro: '#0930D0',
  comercial: '#0a7f4a',
  operacional: '#B07500',
  administrativo: '#0EA5E9',
  marketing: '#E1306C',
  atividades: '#7C3AED',
  admin: '#B91626',
};

function AdminPerfisInner() {
  const { message, modal } = AntApp.useApp();
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [abas, setAbas] = useState<Aba[]>([]);
  const [operacoes, setOperacoes] = useState<Operacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState<Perfil | null>(null);
  const [mostrarNovo, setMostrarNovo] = useState(false);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      // Carrega perfis+abas e operações em paralelo
      const [rPerfis, rOps] = await Promise.all([
        axios.get(`${API_URL}/admin/perfis?incluir_abas=1`,
          { headers: { Authorization: token } }),
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'adm_listar_permissoes' }, { headers: { Authorization: token } }),
      ]);
      const data = rPerfis.data.body ? JSON.parse(rPerfis.data.body) : rPerfis.data;
      const p = Array.isArray(data) ? data : (data.perfis || []);
      const a = Array.isArray(data) ? [] : (data.abas || []);
      const ops = rOps.data.body ? JSON.parse(rOps.data.body) : rOps.data;
      setPerfis(p); setAbas(a); setOperacoes(Array.isArray(ops) ? ops : []);
    } catch (e: any) {
      message.error('Erro ao carregar: ' + (e.response?.data?.erro || e.message));
    } finally { setLoading(false); }
  }

  function excluir(p: Perfil) {
    if (p.nome === 'admin') { message.warning('Não é permitido excluir o perfil admin.'); return; }
    if (p.usuarios_count > 0) {
      message.warning(`Este perfil tem ${p.usuarios_count} usuário(s). Remova antes de excluir.`);
      return;
    }
    modal.confirm({
      title: `Excluir o perfil "${p.nome}"?`,
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/admin/perfis/${encodeURIComponent(p.nome)}`,
            { headers: { Authorization: token } });
          message.success('Perfil excluído.');
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.erro || e.message));
        }
      },
    });
  }

  const columns: ColumnsType<Perfil> = [
    {
      title: 'Perfil', dataIndex: 'nome', key: 'nome',
      render: (v, r) => (
        <Space size={4}>
          <Text strong style={{ color: '#020D7F' }}>{v}</Text>
          {r.nome === 'admin' && <Tag color="warning" style={{ margin: 0 }}>sistema</Tag>}
        </Space>
      ),
    },
    {
      title: 'Descrição', dataIndex: 'descricao', key: 'desc',
      render: (v) => v || <Text type="secondary" italic>sem descrição</Text>,
    },
    { title: 'Usuários', dataIndex: 'usuarios_count', key: 'u', width: 80, align: 'center' },
    { title: 'Abas', key: 'a', width: 70, align: 'center', render: (_, r) => r.permissoes.length },
    {
      title: 'Operações', key: 'ops', width: 90, align: 'center',
      render: (_, r) => operacoes.filter(op => op.grupos.includes(r.nome)).length,
    },
    {
      title: 'Ações', key: 'acoes', width: 170, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(r)}>Editar</Button>
          <Button size="small" danger icon={<DeleteOutlined />} disabled={r.nome === 'admin'}
            onClick={() => excluir(r)}>Excluir</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Perfis definem (1) quais <Text strong>abas</Text> o usuário enxerga no menu e
          (2) quais <Text strong>operações</Text> ele pode executar (ex.: excluir cliente,
          fechar mês). <Text strong>admin</Text> sempre tem acesso total.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setMostrarNovo(true)}>
          Novo perfil
        </Button>
      </Flex>

      <Table
        rowKey="nome"
        columns={columns}
        dataSource={perfis}
        loading={loading}
        size="small"
        pagination={false}
      />

      {(editando || mostrarNovo) && (
        <FormPerfil perfil={editando} abas={abas} operacoes={operacoes}
          onClose={() => { setEditando(null); setMostrarNovo(false); }}
          onSaved={async () => { await carregar(); setEditando(null); setMostrarNovo(false); }}
        />
      )}
    </div>
  );
}

function FormPerfil({ perfil, abas, operacoes, onClose, onSaved }: {
  perfil: Perfil | null;
  abas: Aba[];
  operacoes: Operacao[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = AntApp.useApp();
  const novo = !perfil;
  const [nome, setNome] = useState(perfil?.nome || '');
  const [descricao, setDescricao] = useState(perfil?.descricao || '');
  const [permissoes, setPermissoes] = useState<Set<string>>(new Set(perfil?.permissoes || []));
  // Operações que esse perfil pode executar (subset de operacoes[i].operacao_id)
  const [opsPermitidas, setOpsPermitidas] = useState<Set<string>>(() => {
    if (!perfil) return new Set();
    return new Set(operacoes.filter(op => op.grupos.includes(perfil.nome)).map(op => op.operacao_id));
  });
  const [salvando, setSalvando] = useState(false);

  function toggle(abaId: string) {
    setPermissoes(prev => {
      const n = new Set(prev);
      if (n.has(abaId)) n.delete(abaId); else n.add(abaId);
      return n;
    });
  }
  function toggleModulo(_modulo: string, abasDoModulo: Aba[]) {
    const ids = abasDoModulo.map(a => a.id);
    const todasMarcadas = ids.every(id => permissoes.has(id));
    setPermissoes(prev => {
      const n = new Set(prev);
      if (todasMarcadas) ids.forEach(id => n.delete(id));
      else ids.forEach(id => n.add(id));
      return n;
    });
  }
  function toggleOp(opId: string) {
    setOpsPermitidas(prev => {
      const n = new Set(prev);
      if (n.has(opId)) n.delete(opId); else n.add(opId);
      return n;
    });
  }
  function toggleModuloOps(opsDoModulo: Operacao[]) {
    const ids = opsDoModulo.map(o => o.operacao_id);
    const todasMarcadas = ids.every(id => opsPermitidas.has(id));
    setOpsPermitidas(prev => {
      const n = new Set(prev);
      if (todasMarcadas) ids.forEach(id => n.delete(id));
      else ids.forEach(id => n.add(id));
      return n;
    });
  }

  async function salvar() {
    if (!nome.trim()) { message.warning('Nome do perfil é obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };

      // 1) Salva perfil (cria ou atualiza) — abas visíveis vão dentro do payload
      const payload = { nome: nome.trim(), descricao: descricao.trim(), permissoes: Array.from(permissoes) };
      if (novo) {
        await axios.post(`${API_URL}/admin/perfis`, payload, { headers });
      } else {
        await axios.put(`${API_URL}/admin/perfis/${encodeURIComponent(perfil!.nome)}`, payload, { headers });
      }

      // 2) Sync de operações: pra cada operação que mudou (entrou ou saiu),
      // chama adm_atualizar_permissao com a lista atualizada de grupos.
      // O backend já preserva 'admin' automaticamente.
      const perfilNome = (novo ? nome.trim() : perfil!.nome);
      const opsAlteradas = operacoes.filter(op => {
        const antes = op.grupos.includes(perfilNome);
        const depois = opsPermitidas.has(op.operacao_id);
        return antes !== depois;
      });
      if (opsAlteradas.length > 0) {
        await Promise.all(opsAlteradas.map(op => {
          const novosGrupos = opsPermitidas.has(op.operacao_id)
            ? [...op.grupos, perfilNome]
            : op.grupos.filter(g => g !== perfilNome);
          return axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'adm_atualizar_permissao', operacao_id: op.operacao_id, grupos: novosGrupos },
            { headers });
        }));
      }

      message.success(`${novo ? 'Perfil criado' : 'Perfil atualizado'}.${opsAlteradas.length > 0 ? ` ${opsAlteradas.length} operação(ões) sincronizada(s).` : ''}`);
      onSaved();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  // Agrupa abas por módulo
  const modulos: Record<string, Aba[]> = {};
  for (const a of abas) { (modulos[a.modulo] = modulos[a.modulo] || []).push(a); }

  // Conjunto de módulos que têm AO MENOS UMA aba marcada no perfil.
  // Operações só são exibidas para esses módulos — se o perfil não tem nenhuma
  // aba do Comercial, não faz sentido oferecer operações do Comercial.
  const modulosAtivos = new Set<string>();
  for (const a of abas) {
    if (permissoes.has(a.id)) modulosAtivos.add(a.modulo);
  }
  // Agrupa operações por módulo, filtrando só pelos módulos ativos
  const modulosOps: Record<string, Operacao[]> = {};
  for (const o of operacoes) {
    if (!modulosAtivos.has(o.modulo)) continue;
    (modulosOps[o.modulo] = modulosOps[o.modulo] || []).push(o);
  }

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={salvar}
      okText={novo ? 'Criar perfil' : 'Salvar'}
      cancelText="Cancelar"
      confirmLoading={salvando}
      title={novo ? 'Novo perfil' : `Editar perfil: ${perfil!.nome}`}
      width={820}
      destroyOnClose
    >
      <Form layout="vertical" size="small">
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Nome do perfil"
              required
              extra={(() => {
                if (novo) return 'Use letras, números, _ ou -.';
                if (perfil!.nome === 'admin') return 'O perfil "admin" não pode ser renomeado (safeguard).';
                if (perfil!.usuarios_count > 0) return `Bloqueado: ${perfil!.usuarios_count} usuário(s) usam este perfil. Para renomear, remova todos os usuários do grupo no Cognito primeiro.`;
                return 'Renomeando este perfil: o grupo Cognito de mesmo nome será criado, as permissões de operações migradas, e o grupo antigo removido.';
              })()}
            >
              <Input value={nome} onChange={e => setNome(e.target.value)}
                disabled={!novo && (perfil!.nome === 'admin' || perfil!.usuarios_count > 0)}
                placeholder="ex.: financeiro_view" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label="Descrição">
              <Input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Para que serve este perfil?" />
            </Form.Item>
          </Col>
        </Row>

        <Collapse size="small" defaultActiveKey={['abas', 'ops']}
          items={[
            {
              key: 'abas',
              label: <Text strong style={{ fontSize: 12, color: '#555' }}>
                📑 Abas visíveis no menu ({permissoes.size}/{abas.length})
              </Text>,
              children: (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {Object.entries(modulos).map(([modId, abasMod]) => {
                    const todasMarcadas = abasMod.every(a => permissoes.has(a.id));
                    const algumaMarcada = abasMod.some(a => permissoes.has(a.id));
                    return (
                      <Card key={modId} size="small"
                        style={{ borderColor: `${MODULO_COR[modId]}55`, background: `${MODULO_COR[modId]}08` }}
                      >
                        <Flex align="center" gap={6}>
                          <Checkbox checked={todasMarcadas}
                            indeterminate={!todasMarcadas && algumaMarcada}
                            onChange={() => toggleModulo(modId, abasMod)}>
                            <Text strong style={{ color: MODULO_COR[modId], fontSize: 12 }}>
                              {MODULO_LABEL[modId] || modId}
                            </Text>
                          </Checkbox>
                          <Text type="secondary" style={{ fontSize: 10 }}>
                            ({abasMod.filter(a => permissoes.has(a.id)).length} / {abasMod.length})
                          </Text>
                        </Flex>
                        <Row gutter={[4, 4]} style={{ marginTop: 6, paddingLeft: 20 }}>
                          {abasMod.map(a => (
                            <Col key={a.id} xs={12} sm={8} md={6}>
                              <Checkbox checked={permissoes.has(a.id)} onChange={() => toggle(a.id)}>
                                <Text style={{ fontSize: 12, color: permissoes.has(a.id) ? '#222' : '#888' }}>
                                  {a.label}
                                </Text>
                              </Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Card>
                    );
                  })}
                </Space>
              ),
            },
            {
              key: 'ops',
              label: (() => {
                const opsVisiveis = operacoes.filter(o => modulosAtivos.has(o.modulo));
                const opsMarcadasVisiveis = opsVisiveis.filter(o => opsPermitidas.has(o.operacao_id)).length;
                return <Text strong style={{ fontSize: 12, color: '#555' }}>
                  🔐 Operações permitidas ({opsMarcadasVisiveis}/{opsVisiveis.length})
                </Text>;
              })(),
              children: operacoes.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Nenhuma operação cadastrada. (Catálogo é populado pelo backend conforme features são liberadas.)
                </Text>
              ) : Object.keys(modulosOps).length === 0 ? (
                <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                  Selecione abas de algum módulo acima para configurar as operações desse módulo.
                </Text>
              ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {Object.entries(modulosOps).map(([modId, opsMod]) => {
                    const ids = opsMod.map(o => o.operacao_id);
                    const todas = ids.every(id => opsPermitidas.has(id));
                    const alguma = ids.some(id => opsPermitidas.has(id));
                    return (
                      <Card key={modId} size="small">
                        <Flex align="center" gap={6}>
                          <Checkbox checked={todas}
                            indeterminate={!todas && alguma}
                            onChange={() => toggleModuloOps(opsMod)}>
                            <Text strong style={{ fontSize: 12 }}>
                              {OP_MODULO_LABEL[modId] || modId}
                            </Text>
                          </Checkbox>
                          <Text type="secondary" style={{ fontSize: 10 }}>
                            ({opsMod.filter(o => opsPermitidas.has(o.operacao_id)).length} / {opsMod.length})
                          </Text>
                        </Flex>
                        <div style={{ marginTop: 6, paddingLeft: 20 }}>
                          {opsMod.map(op => (
                            <div key={op.operacao_id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                              <Checkbox checked={opsPermitidas.has(op.operacao_id)}
                                onChange={() => toggleOp(op.operacao_id)}>
                                <Text style={{ fontSize: 12, color: opsPermitidas.has(op.operacao_id) ? '#222' : '#888' }}>
                                  {op.descricao}
                                </Text>
                              </Checkbox>
                              <Tag color={OP_RISCO_TAG[op.risco]} style={{ fontSize: 9, padding: '0 5px', lineHeight: '16px', marginLeft: 'auto' }}>
                                {op.risco}
                              </Tag>
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })}
                </Space>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
}

export default function AdminPerfis() {
  return (
    <AntApp>
      <AdminPerfisInner />
    </AntApp>
  );
}
